import React from "react";
import { useMutation, QueryKey, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { solicitudes } from "api/solicitudes";
import {
  SolicitudConsumoAnalisisResponse,
  DesistirSolicitudConsumoRequest,
} from "api/types/originacion/services/analisisSolicitudTypes";

import { EEstadoSolicitudConsumo } from "api/types/originacion/businessTypes";

import useToken from "components/hooks/useToken";

import DesistirSolicitud from "./actions/DesistirSolicitud";
import AprobarSolicitud from "./actions/AprobarSolicitud";
import { useFlags } from "launchdarkly-react-client-sdk";

interface ISolicitudAnalisisActions {
  solicitud: SolicitudConsumoAnalisisResponse;
}

interface AprobarSolicitudRequest {
  id: number;
  tipoCallback: number;
  estadoTicket: number;
}

const SolicitudRevisionActions = ({ solicitud }: ISolicitudAnalisisActions) => {
  const location = useLocation();
  const { desistirCredito, aprobarCredito, mutationLoading } =
    useAnalisisActions();
  const { yenConsumoAprobacion } = useFlags();

  const canDesistirOrRechazar =
    solicitud.estado !== EEstadoSolicitudConsumo.Liquidada &&
    solicitud.estado !== EEstadoSolicitudConsumo.Rechazada &&
    solicitud.estado !== EEstadoSolicitudConsumo.Desistida &&
    yenConsumoAprobacion;

  const canAprobar =
    solicitud.estado === EEstadoSolicitudConsumo.En_Analisis &&
    yenConsumoAprobacion;

  const isObOnlineView = location.pathname.startsWith("/ob-online");

  const showAprobarButton = canAprobar && isObOnlineView;

  const handleDesistir = (comentario: string) => {
    desistirCredito({ comentario, id: solicitud.id });
  };

  const handleAprobar = (tipoCallback: number, estadoTicket: number) => {
    aprobarCredito({ tipoCallback, estadoTicket, id: solicitud.id });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
      {canDesistirOrRechazar && (
        <DesistirSolicitud
          handleDesistir={handleDesistir}
          isLoading={mutationLoading}
          solicitud={solicitud}
        />
      )}
      {showAprobarButton && (
        <AprobarSolicitud
          handleAprobar={handleAprobar}
          isLoading={mutationLoading}
          solicitud={solicitud}
        />
      )}
    </div>
  );
};
export default SolicitudRevisionActions;

const useAnalisisActions = () => {
  const token = useToken();
  const queryClient = useQueryClient();

  //desistir
  const desistirPrestamoRequest = async (
    request: DesistirSolicitudConsumoRequest
  ) => {
    if (!token) throw new Error("Must be authenticated");
    await solicitudes(token).desistirConsumo(request);
  };
  const { mutate: desistirCredito, isLoading: isLoadingDesistir } = useMutation(
    desistirPrestamoRequest,
    {
      onMutate: ({ id }) =>
        updateCacheSolicitud(id, EEstadoSolicitudConsumo.Desistida),
      onSuccess: (_, { id }) => invalidateCacheSolicitud(id),
    }
  );

  const aprobarCreditoRequest = async (request: AprobarSolicitudRequest) => {
    if (!token) throw new Error("Must be authenticated");
    await solicitudes(token).aprobarConsumo(request);
  };
  const { mutate: aprobarCredito, isLoading: isLoadingAprobar } = useMutation(
    aprobarCreditoRequest,
    {
      onMutate: ({ id }) =>
        updateCacheSolicitud(id, EEstadoSolicitudConsumo.Aprobada),
      onSuccess: (_, { id }) => invalidateCacheSolicitud(id),
    }
  );

  const updateCacheSolicitud = (
    id: number,
    estado: EEstadoSolicitudConsumo
  ) => {
    const cacheKey = ["detalleSolicitudConsumo", { id }] as QueryKey;
    const prevSolicitud = queryClient.getQueryData(
      cacheKey
    ) as SolicitudConsumoAnalisisResponse;
    queryClient.setQueryData(cacheKey, {
      ...prevSolicitud,
      estado: estado,
    });
  };

  const invalidateCacheSolicitud = (id: number) => {
    const cacheKey = ["detalleSolicitudConsumo", { id }] as QueryKey;
    queryClient.invalidateQueries(cacheKey);
  };

  return {
    desistirCredito,
    aprobarCredito,
    mutationLoading: isLoadingDesistir || isLoadingAprobar,
  };
};
